import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import type TicketDeskStore from "../TicketDeskStore";
import type { TicketClientOption } from "../TicketDeskStore";

/**
 * Tickets → Unmatched support mail: the support e-mail HQ could not file on
 * its own (#2237), and the decision a person makes about each message.
 *
 * The API (`ticket-intake`, packages/api/src/services/organization/
 * ticket-intake/ticket-intake.ts) is the boundary: only operators, admins and
 * the configured support-desk staff member may read or place this mail, and
 * every place is a compare-and-set on the message still waiting. This store
 * only shows what the API returns and words its refusals.
 *
 * RULE 1 HOLDS: the rows are the `ticket-intake` ServiceStore's own entities
 * (`replaceOnFind`, so a find is the whole list), narrowed to the mode — a
 * message placed here leaves "Waiting" the moment the API's answer replaces
 * its entity. Only the find's `total` is kept here.
 */
export const SUPPORT_MAIL_SERVICE = "ticket-intake";
export const SUPPORT_MAIL_PAGE = 100;

export type SupportMailMode = "waiting" | "handled";
/** `attach`: put a placed message's files on its ticket when an earlier attempt could not. */
export type SupportMailAction = "assign" | "append" | "discard" | "attach";

/** Kept, but not on the ticket yet: neither a document nor a reason it was not kept. */
export const isPending = (a: IntakeAttachmentRecord): boolean =>
  !a.documentId && !a.note;

export interface IntakeAttachmentRecord {
  fileName: string;
  mimeType: string;
  size: number;
  documentId: string | null;
  /** Not kept, and why. */
  note: string | null;
}

export interface IntakeRecord {
  id: number;
  channel: string;
  status: string;
  clientId: string | null;
  clientName: string | null;
  ticketId: number | null;
  fromName: string | null;
  fromEmail: string | null;
  subject: string | null;
  /** Plain text. Rendered as text, never as HTML. */
  bodyText: string | null;
  triageReason: string | null;
  suggestedClientId: string | null;
  suggestedClientName: string | null;
  suggestedTicketId: number | null;
  senderVerified: boolean;
  verification: string | null;
  attachments: IntakeAttachmentRecord[];
  receivedAt: string | null;
  resolvedAt: string | null;
  resolvedBy: number | null;
  resolvedByName: string | null;
  resolutionNote: string | null;
}

/** "Kim Lee <kim@example.com>", the address, the name, or "an unknown sender". */
export const senderOf = (
  r: Pick<IntakeRecord, "fromName" | "fromEmail">,
): string => {
  const name = (r.fromName ?? "").trim();
  const email = (r.fromEmail ?? "").trim();
  if (name && email) return `${name} <${email}>`;
  return email || name || "an unknown sender";
};

/** What became of a handled message, as a phrase. */
export const outcomeOf = (
  r: Pick<IntakeRecord, "status" | "ticketId" | "clientName">,
): string => {
  switch (r.status) {
    case "assigned":
      return `Filed as TKT-${r.ticketId}${r.clientName ? ` for ${r.clientName}` : ""}`;
    case "appended":
      return `Added to TKT-${r.ticketId}`;
    case "discarded":
      return "Discarded";
    case "filed":
      return `Filed as TKT-${r.ticketId}`;
    default:
      return r.status;
  }
};

export default class SupportMailStore {
  rootStore: RootStore;

  mode: SupportMailMode = "waiting";
  /** The API's total for the mode — null until a page arrives. */
  total: number | null = null;
  loading = false;
  loaded = false;
  error: string | null = null;

  selectedId: number | null = null;
  clientDraft = "";
  ticketDraft = "";
  noteDraft = "";
  acting: SupportMailAction | null = null;
  actionError: string | null = null;

  private seq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore(
        SUPPORT_MAIL_SERVICE,
      ) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get desk(): TicketDeskStore | null {
    return (
      (this.rootStore as unknown as { ticketDeskStore?: TicketDeskStore })
        .ticketDeskStore ?? null
    );
  }

  /** The list: the ServiceStore's rows for this mode, newest first. */
  get rows(): IntakeRecord[] {
    const all = (
      (this.service?.entities ?? []) as unknown as IntakeRecord[]
    ).filter((r) => r && typeof r.id === "number");
    const inMode = all.filter((r) =>
      this.mode === "waiting"
        ? r.status === "unmatched"
        : r.status !== "unmatched" && r.status !== "received",
    );
    const key = (r: IntakeRecord) =>
      (this.mode === "waiting" ? r.receivedAt : r.resolvedAt) ?? "";
    return inMode.sort((a, b) => key(b).localeCompare(key(a)));
  }

  get selected(): IntakeRecord | null {
    if (this.selectedId == null) return null;
    const all = (this.service?.entities ?? []) as unknown as IntakeRecord[];
    return all.find((r) => r.id === this.selectedId) ?? null;
  }

  /** Live clients, A→Z — the desk's own picker list (TicketDeskStore.clients). */
  get clients(): TicketClientOption[] {
    return this.desk?.clients ?? [];
  }

  get isTruncated(): boolean {
    return this.total !== null && this.total > this.rows.length;
  }

  get canAssign(): boolean {
    return (
      this.selected?.status === "unmatched" &&
      Boolean(this.clientDraft) &&
      this.acting === null
    );
  }

  get canAppend(): boolean {
    return (
      this.selected?.status === "unmatched" &&
      /^(?:tkt-?)?\d{1,9}$/i.test(this.ticketDraft.trim()) &&
      this.acting === null
    );
  }

  get canDiscard(): boolean {
    return this.selected?.status === "unmatched" && this.acting === null;
  }

  /** A placed message whose kept files are not all on its ticket yet (the API keeps them; this retries). */
  get canRetryAttach(): boolean {
    const row = this.selected;
    return (
      !!row &&
      row.status !== "unmatched" &&
      row.ticketId != null &&
      row.attachments.some(isPending) &&
      this.acting === null
    );
  }

  setMode(mode: SupportMailMode) {
    if (mode === this.mode) return;
    this.mode = mode;
    this.selectedId = null;
    void this.load();
  }

  /**
   * Load (or reload) the mode's list. Never rejects: a failure — including
   * the API's 403 for someone who may not see this mail — lands on `error`,
   * so the page can tell "nothing is waiting" from "this could not be read".
   */
  async load(): Promise<void> {
    const service = this.service;
    const seq = ++this.seq;
    this.loading = true;
    this.error = null;
    try {
      const res = await findOrThrow<
        { total?: number; data?: IntakeRecord[] } | IntakeRecord[] | null
      >(service, {
        query: {
          status: this.mode === "waiting" ? "unmatched" : "handled",
          $limit: SUPPORT_MAIL_PAGE,
        },
      });
      if (seq !== this.seq) return;
      if (res == null) throw new Error("The support mail could not be loaded.");
      runInAction(() => {
        this.total = Array.isArray(res)
          ? res.length
          : typeof res.total === "number"
            ? res.total
            : null;
        this.loaded = true;
      });
    } catch (err) {
      if (seq !== this.seq || isSessionEnded(err)) return;
      runInAction(() => {
        this.error = loadErrorMessage(
          err,
          "The support mail could not be loaded.",
        );
        this.loaded = true;
      });
    } finally {
      if (seq === this.seq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /** Open one message, with HQ's suggestion pre-filled; the clients load for the picker. */
  select(id: number) {
    this.selectedId = id;
    const row = this.selected;
    this.clientDraft = row?.suggestedClientId ?? "";
    this.ticketDraft = row?.suggestedTicketId
      ? String(row.suggestedTicketId)
      : "";
    this.noteDraft = "";
    this.actionError = null;
    void this.desk?.loadSubjects();
  }

  close() {
    if (this.acting) return;
    this.selectedId = null;
    this.actionError = null;
  }

  setClientDraft(value: string) {
    this.clientDraft = value;
  }

  setTicketDraft(value: string) {
    this.ticketDraft = value;
  }

  setNoteDraft(value: string) {
    this.noteDraft = value;
  }

  /**
   * File it for the chosen client, add it to a ticket, or discard it. Asks the
   * ServiceStore to THROW (the library swallows a refused patch into a null
   * by default, which would read as "done"): a refusal — someone placed it
   * first (409), the person may not (403), the ticket is unknown (404) —
   * keeps the drawer open with the API's own sentence.
   *
   * @returns true when the API accepted the decision.
   */
  async place(action: SupportMailAction): Promise<boolean> {
    const row = this.selected;
    const service = this.service;
    if (!row || !service || this.acting) return false;
    if (action === "assign" && !this.canAssign) return false;
    if (action === "append" && !this.canAppend) return false;
    if (action === "attach" && !this.canRetryAttach) return false;
    const payload: Record<string, unknown> = { action };
    if (action === "assign") payload.clientId = this.clientDraft;
    if (action === "append")
      payload.ticketId = Number(this.ticketDraft.trim().replace(/^tkt-?/i, ""));
    const note = this.noteDraft.trim();
    if (note) payload.note = note;
    const ended = sessionMark(this.rootStore);
    this.acting = action;
    this.actionError = null;
    try {
      const updated = (await service.patch(
        row.id,
        payload as never,
        { throwOnError: true } as never,
      )) as unknown as IntakeRecord | null | undefined;
      if (ended()) return false;
      if (!updated) throw new Error("HQ did not confirm the change.");
      runInAction(() => {
        this.selectedId = null;
        if (this.total !== null && this.mode === "waiting")
          this.total = Math.max(0, this.total - 1);
      });
      const stillPending = (updated.attachments ?? []).filter(isPending).length;
      this.rootStore.notificationStore?.addSuccessNotification(
        action === "discard"
          ? "Discarded. Nothing was sent to the sender."
          : action === "attach"
            ? stillPending
              ? `${stillPending} file${stillPending === 1 ? " is" : "s are"} still not on TKT-${updated.ticketId}; try again later.`
              : `The files are on TKT-${updated.ticketId} now.`
            : `${outcomeOf(updated)}.`,
      );
      return true;
    } catch (err) {
      if (ended() || isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError = loadErrorMessage(
          err,
          "The message could not be placed.",
        );
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.acting = null;
        });
      }
    }
  }

  /** Open a placed message's ticket on the desk. */
  openTicket(id: number) {
    void this.rootStore.routerStore?.goTo?.(
      "Tickets",
      {},
      { open: String(id) },
    );
  }

  /** Drop everything — leaving the page, and on sign-out (RootStore purges every store with a reset()). */
  reset() {
    this.seq += 1;
    this.mode = "waiting";
    this.total = null;
    this.loading = false;
    this.loaded = false;
    this.error = null;
    this.selectedId = null;
    this.clientDraft = "";
    this.ticketDraft = "";
    this.noteDraft = "";
    this.acting = null;
    this.actionError = null;
  }
}
